import type {
  ApiContext,
  ApiNotAuthorized,
  CardInfo,
  InvalidFormField,
  Scan,
  ScanCandidateUnknown,
  ScanConfirmation,
  ScanConfirmOptions,
  ScanEdit,
  ScanNotFound,
  ScanNotReviewable,
  StoreNotFound,
} from "@hobbyshopify/backend-client";
import { newScanAttemptId } from "@hobbyshopify/backend-client";
import type { AsyncResult } from "operation-result.js";
import type { AuthContextProvider, AuthHandler } from "trunk.js";
import { applyScanEvent, initialScanProgress, type ScanProgress } from "../primitives/scan-progress";
import { shopEntryId } from "../primitives/workspace";
import type { CustomersRepository } from "../repositories/customers-repository";
import type { ReviewQueueRepository } from "../repositories/review-queue-repository";
import type { BackendApi } from "./api";

export function loadScan(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  scanId: string;
}): AsyncResult<Scan, ApiNotAuthorized | StoreNotFound | ScanNotFound> {
  return o.authHandler.callApi((ctx) => o.client.scans.get(ctx, o.shopId, o.scanId, { enableNoMatch: true }));
}

/**
 * Saves the reading while the reviewer works, so the match and the price follow
 * the correction instead of waiting for confirm. The answer says whether the
 * edit forced a fresh pricing lookup.
 */
export function saveScanDraft(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  scanId: string;
  corrections: Partial<CardInfo>;
}): AsyncResult<ScanEdit, ApiNotAuthorized | StoreNotFound | ScanNotFound | ScanNotReviewable> {
  return o.authHandler.callApi((ctx) => o.client.scans.edit(ctx, o.shopId, o.scanId, o.corrections));
}

/** Makes one of the scan's candidates the pick. No lookup follows; the scan comes back re-matched. */
export function pickScanCandidate(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  scanId: string;
  productId: string;
}): AsyncResult<ScanEdit, ApiNotAuthorized | StoreNotFound | ScanNotFound | ScanNotReviewable | ScanCandidateUnknown> {
  return o.authHandler.callApi((ctx) => o.client.scans.pick(ctx, o.shopId, o.scanId, o.productId));
}

/** What a confirm or reject touches elsewhere: the lists that showed the scan. */
export interface ScanReviewSideEffects {
  reviewQueueRepository: ReviewQueueRepository;
  customersRepository: CustomersRepository;
}

/** Confirms a scan as a card: the only path that creates one. */
export async function confirmScan(
  o: ScanReviewSideEffects & {
    client: BackendApi;
    authHandler: AuthHandler<ApiContext>;
    shopId: string;
    scanId: string;
    body: ScanConfirmOptions;
  },
): AsyncResult<
  ScanConfirmation,
  ApiNotAuthorized | StoreNotFound | ScanNotFound | ScanNotReviewable | InvalidFormField
> {
  const r = await o.authHandler.callApi((ctx) => o.client.scans.confirm(ctx, o.shopId, o.scanId, o.body));
  if (r.ok) markReviewed(o);
  return r;
}

export async function rejectScan(
  o: ScanReviewSideEffects & {
    client: BackendApi;
    authHandler: AuthHandler<ApiContext>;
    shopId: string;
    scanId: string;
  },
): AsyncResult<void, ApiNotAuthorized | StoreNotFound | ScanNotFound> {
  const r = await o.authHandler.callApi((ctx) => o.client.scans.reject(ctx, o.shopId, o.scanId));
  if (r.ok) markReviewed(o);
  return r;
}

function markReviewed(o: ScanReviewSideEffects): void {
  o.reviewQueueRepository.markStale();
  o.customersRepository.markStale();
}

/**
 * Runs one scan to completion, reporting progress as it goes.
 *
 * A stream, not a `Result`, so it takes the session directly rather than the
 * `AuthHandler`: there is no single answer a 401 could be retried into.
 *
 * Resolves with the saved scan, or throws if the pipeline reported an error,
 * which arrives as a `scan.error` frame rather than a rejection, because by then
 * the response headers are long gone.
 *
 * Aborting stops the listening, not the scan: the backend links the ShopScan
 * before the pipeline runs, so it still completes and persists.
 */
export async function runScan(o: {
  client: BackendApi;
  session: AuthContextProvider<ApiContext>;
  shopId: string;
  /** The counter the card was scanned at; the scan is filed under it. */
  locationId: string | null;
  images: { imageUrl: string; backImageUrl?: string; customerId?: string; attemptId?: string };
  onProgress?: (progress: ScanProgress) => void;
  signal?: AbortSignal;
}): Promise<Scan> {
  const ctx = await o.session.authorizeCall();
  let state: ScanProgress = { ...initialScanProgress(), status: "scanning" };
  o.onProgress?.(state);

  for await (const event of o.client.scans.startStream(
    ctx,
    shopEntryId(o.shopId, o.locationId),
    { ...o.images, enableNoMatch: true, attemptId: o.images.attemptId ?? newScanAttemptId() },
    {
      signal: o.signal,
    },
  )) {
    state = applyScanEvent(state, event);
    o.onProgress?.(state);
  }

  if (state.status === "error" || !state.result) {
    throw new Error(state.error ?? "The scan stream ended before completion.");
  }
  return state.result;
}
